/** @format */
import isPropValid from '@emotion/is-prop-valid';
import { Button } from '@mpb-com/feather/components/atoms/button';
import { Heading } from '@mpb-com/feather/components/atoms/heading';
import { HeadingSize } from '@mpb-com/feather/components/atoms/heading/types';
import { Field, FieldProps, Form, Formik, FormikHelpers } from 'formik';
import { useTranslation } from 'next-i18next';
import React, { FunctionComponent } from 'react';
import styled from 'styled-components';
import { object } from 'yup';

import { email } from '@toucan/client/global/user';

// eslint-disable-next-line boundaries/element-types -- This should be using the feather input component
import { Input } from '@toucan/client/shared/components/input';

import { styles } from './request-password-reset-form.styles';

// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions -- Legacy code: do not use as an example.
export interface RequestPasswordResetFormValues {
    email: string;
}

// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions -- Legacy code: do not use as an example.
export interface RequestPasswordResetFormProps {
    /**
     * Selection ID for test purposes.
     */
    'data-testid'?: string;

    /**
     * Custom className
     */
    className?: string;

    /**
     * The email to pre-fill in the form
     */
    initialEmail?: string;

    /**
     * A function that will fire when the form is submitted
     */
    onRequestPasswordReset: (
        formValues: RequestPasswordResetFormValues,
        formActions: FormikHelpers<RequestPasswordResetFormValues>
    ) => void;
}

// eslint-disable-next-line @mpb-com/mpb/require-types-file -- Legacy code: do not use as an example.
export type RequestPasswordResetFormStyleProps = Omit<
    RequestPasswordResetFormProps,
    'onRequestPasswordReset'
>;

const StyledRequestPasswordResetForm = styled('div').withConfig({
    shouldForwardProp: (prop) => isPropValid(prop),
})<RequestPasswordResetFormStyleProps>`
    ${styles}
`;

export const RequestPasswordResetForm: FunctionComponent<
    RequestPasswordResetFormProps
> = ({
    onRequestPasswordReset,
    initialEmail = '',
    ...otherProps
}: RequestPasswordResetFormProps) => {
    const { t } = useTranslation();

    const initialValues = {
        email: initialEmail,
    };

    const validationSchema = object().shape({
        email: email(t),
    });

    return (
        <StyledRequestPasswordResetForm {...otherProps}>
            <Heading size={HeadingSize.H2} className="heading">
                {t('So, you need to reset your password.')}
            </Heading>
            <p className="info-text">
                {t(
                    "Not to worry. Pop your email address in, we'll send you a link so you can create a fresh one."
                )}
            </p>
            <Formik
                initialValues={initialValues}
                onSubmit={onRequestPasswordReset}
                validationSchema={validationSchema}
            >
                {({ isSubmitting, errors, touched }) => (
                    <Form
                        className="form"
                        data-testid="request-password-reset-form"
                    >
                        <Field name="email">
                            {({ field }: FieldProps) => (
                                <Input
                                    {...field}
                                    label={t('Email')}
                                    type="email"
                                    // @ts-ignore - Element implicitly has an any type - Touch and Change
                                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- Legacy code: do not use as an example.
                                    error={errors[field.name]}
                                    // @ts-ignore - Element implicitly has an any type - Touch and Change
                                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- Legacy code: do not use as an example.
                                    touched={touched[field.name]}
                                    required={true}
                                    className="email-address"
                                />
                            )}
                        </Field>

                        <div className="submit">
                            <Button
                                type="submit"
                                disabled={isSubmitting}
                                data-testid="request-password-reset-form__submit"
                            >
                                {t('Reset password')}
                            </Button>
                        </div>
                    </Form>
                )}
            </Formik>
        </StyledRequestPasswordResetForm>
    );
};
